/* © CNES */
import { QueryFunctionContext } from '@tanstack/query-core';
import axios, { AxiosProgressEvent } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { IDENTIFIER } from '../../components/shared/Constants';
import { CollectionsForUser } from '../../models/CollectionsForUser';
import { ItemFilter } from '../../models/Filter';
import { GeoJsonPolygon } from '../../models/GeoJsonPolygon';
import { ItemsAvailabilities } from '../../models/ItemAvailability';
import { apiConfiguration } from '../APIConfiguration';
import { authenticationAPI } from '../authentication/AuthenticateAPI';
import { handleHttpError } from '../common';
import responseCollectionAcces from '../mock/response_collection_user.json';
import responseMockItem from '../mock/response_item.json';
import responseMock from '../mock/response_items.json';
import responseMockItemsAvailabilities from '../mock/response_items_availabilities.json';
import responseMockItemAvailability from '../mock/response_item_availability.json';
import { buildQueryParams } from './BuildQueryItem';
import { QueryItemsAvailabilitiesModel } from './useGetItemAvailability';

/**
 * Interface for searchItems parameters.
 */
export interface QueryModel {
  params: ItemFilter;
  token?: string | null;
  bbox: number[] | undefined;
  intersects: GeoJsonPolygon | null;
}

/**
 * Request to search items on regards with filters.
 * @param queryKey the query parameters.
 * @returns a Promise containing a list of items filtered.
 */
export const searchItems = async ({
  queryKey,
}: QueryFunctionContext<
  [string, QueryModel | undefined, NavigateFunction, Function]
>) => {
  let res: Response | undefined;
  const [, searchParams, navigate, setError400] = queryKey;
  let queryParams: {} = { query: {} };

  if (process.env.NODE_ENV === 'production') {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...authenticationAPI.getAuthorizationHeaders(),
    });
    if (searchParams) {
      queryParams = buildQueryParams(
        searchParams.params,
        searchParams.bbox,
        searchParams.token,
        searchParams.intersects
      );
    }

    res = await fetch(
      `${apiConfiguration.catalogBaseUrl}${apiConfiguration.apiCatalog}/search`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(queryParams).replaceAll('\\', ''),
      }
    );

    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMock));
  }

  return res.json();
};

/**
 * Request to get details of a specific item on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing details of an item.
 */
export const getItemDetails = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.catalogBaseUrl}${apiConfiguration.apiCatalog}/search`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
        body: JSON.stringify({
          query: {
            [IDENTIFIER]: {
              in: [params.productName],
            },
          },
        }),
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMockItem));
  }
  return res?.json();
};

/**
 * Request to get the product zip file to download
 * @param href url to s3 gateway to download product
 * @param productName the product name
 * @param setError400 function to set error 400
 * @param setDownloadProgress function to set download progress
 * @param navigate function to navigate to another page
 * @param isAsset boolean to check if the product is an asset
 * @return a Promise containing the product zip file
 */
export const downloadProductZipFile = async (
  href: string,
  productName: string,
  setError400: Function,
  setDownloadProgress: Function,
  navigate: NavigateFunction,
  isAsset: boolean
): Promise<any> => {
  if (process.env.NODE_ENV === 'production') {
    var s3location = href.replace("https://s3.geodes-lab.fr/", '')
    return axios({
      url: `${apiConfiguration.apiBaseUrl}/download/${s3location}`,
      //url: `${href}`,
      method: 'GET',
      responseType: 'blob', // important
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
      onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.total) {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setDownloadProgress(percentage);
        }
      },
    })
      .then((res) => {
        const temporaryDownloadLink = document.createElement('a');
        temporaryDownloadLink.href = URL.createObjectURL(res.data);
        let sanitizedFileName = productName.replace(/\./g, '-');

        if (isAsset) {
          sanitizedFileName =
            productName.split('.').length > 1
              ? productName
                  .split('.')
                  .slice(0, -1)
                  .join('.')
                  .replace(/\./g, '-')
              : productName.replace(/\./g, '-');
        }

        temporaryDownloadLink.download = sanitizedFileName;
        document.body.appendChild(temporaryDownloadLink);
        temporaryDownloadLink.click();
        temporaryDownloadLink.parentNode?.removeChild(temporaryDownloadLink);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => {
        setDownloadProgress(null);
      });
  } else {
    return axios({
      url: `../../example.zip`, //your url
      method: 'GET',
      responseType: 'blob', // important
      onDownloadProgress: (progressEvent) => {
        setDownloadProgress(progressEvent.progress);
      },
    })
      .then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);
        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', 'file.zip'); //or any other extension
        document.body.appendChild(link);
        link.click();

        // // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        console.error('error while downloading file : ', error);
        handleHttpError(error.response, navigate, setError400);
      })
      .finally(() => {
        setDownloadProgress(null);
      });
  }
};

interface Item {
  id: string;
  bbox: number[];
  type: string;
  links: Array<{ rel: string; type: string; href: string }>;
  assets: {
    data: Asset;
    quicklook?: Asset;
  };
  properties: Properties;
}
// Définir une interface pour les objets dans X
interface Asset {
  href: string;
  type: string;
  "file:checksum": string;
}

interface Properties {
  "sat:absolute_orbit": number;
}
interface Product {
  id: string;
  files: Array<{ checksum: string; available: boolean }>;
}

interface Products {
  products: Product[];
}
/**
 * Request to get Availability of a specific item on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing the Availability of an item.
 */
export const getItemAvailability = async ({
  queryKey,
}: QueryFunctionContext<any>): Promise<any> => {
  // const [, params, navigate, setError400] = queryKey;
  // // Mock availabilty check
  // let res: Response | undefined;
  // const products: Products = {
  //   "products": []
  // };
  // params.availability.forEach((item : Item) => {
  //   const product = {
  //     "id": item.id,
  //     "files": [
  //       {
  //         "checksum": item.assets.data["file:checksum"],
  //         "available": true
  //       }
  //     ]
  //   };
  //   products.products.push(product);
  // });
  let res: Response | undefined;
  const [, params, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/availability/items/${params.productId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMockItemAvailability));
    return res?.json();
  }
  const products: Products = {
    "products": Object.entries(res?.json()).map(([id, available]) => ({
      id,
      files: [
        {
          "available": available=== "Hot",
          "checksum": "ABC"
        }
      ]
    }))};
  res = new Response(JSON.stringify(products));
  return res?.json();
};

/**
 * Request to get Availabilities of listed items on regards.
 * @param queryKey the query parameters.
 * @returns a Promise containing the Availability of an item.
 */
export const getItemsAvailabilities = async ({
  queryKey,
}: QueryFunctionContext<
  [string, QueryItemsAvailabilitiesModel]
>): Promise<ItemsAvailabilities> => {

  let res: Response | undefined;
  const [, params] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...authenticationAPI.getAuthorizationHeaders(),
    });
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/availability/items/`, 
      {
        method: 'POST',
        headers,
        body: JSON.stringify(params.availability.map(object => object.id)).replaceAll('\\', ''),
    });
    
  } else {
    res = new Response(JSON.stringify(responseMockItemsAvailabilities));
    return res?.json();
  }
  // return res?.json();

  const products: Products = {
    "products": Object.entries(await res?.json()).map(([id, available]) => ({
      id,
      files: [
        {
          "available": available=== "Hot",
          "checksum": "ABC"
        }
      ]
    }))};
    res = new Response(JSON.stringify(products));
    return res?.json();
};

/**
 * get access to the collection.
 * @param queryKey the query parameters.
 * @returns a list of collection.
 */
export const getAccessCollection = async ({
  queryKey,
}: QueryFunctionContext<
  [string, NavigateFunction, Function]
>): Promise<CollectionsForUser> => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;
  if (process.env.NODE_ENV === 'production') {
    res = await fetch(`${apiConfiguration.catalogBaseUrl}${apiConfiguration.apiCatalog}/collections`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
    });
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseCollectionAcces));
  }
  return res.json();
};
